import { SELECTORS, CLASS_NAMES, EVENTS, MISC } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Story Navigation module
 */
export default class StoryNavigation extends MessageBus {
  /**
   * Constructor function of the StoryNavigation class. Must receive one argument, a DOM
   * node representing the story navigation container.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers, and
   * then create the event listeners
   * @returns {object} StoryNavigation A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable().generateNavigation();

    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} StoryNavigation A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.navigationList = this.element.querySelector(
      `.${CLASS_NAMES.STORY_NAVIGATION_LIST}`
    );
    this.dropdown = this.element.querySelector(
      `.${CLASS_NAMES.STORY_NAVIGATION_DROPDOWN}`
    );
    this.bodyContents = document.querySelectorAll(
      `.${CLASS_NAMES.BODY_TEXT_CONTENT}`
    );

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} StoryNavigation A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.navigationList.addEventListener(EVENTS.CLICK, (event) => {
      const clickedElement = event.target.closest('a');
      if (clickedElement) {
        event.preventDefault();
        const id = clickedElement.getAttribute('data-id');
        this.scrollToHeading(id);
      }
    });
    this.dropdown.addEventListener(
      EVENTS.CHANGE,
      this.handleDropdownChange.bind(this)
    );

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} StoryNavigation A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleLinkClick = this.handleLinkClick.bind(this);
    this.handleDropdownChange = this.handleDropdownChange.bind(this);

    return this;
  }

  /**
   * Generate navigation links and dropdown options based on h2 elements in the body content
   * @returns {object} StoryNavigation A reference to the current instance of the class
   * @chainable
   */
  generateNavigation() {
    const excludedSelectors = `${SELECTORS.DIV}.${CLASS_NAMES.INTRODUCTION_INTRO} ${SELECTORS.H2}, ${SELECTORS.H2}.${CLASS_NAMES.THREE_UP_TITLE}, ${SELECTORS.H2}.${CLASS_NAMES.SLIM_QUOTE_QUOTATION}, .${CLASS_NAMES.GEO_LOCATION_COLUMN} ${SELECTORS.H2}`;
    this.bodyContents.forEach((bodyContent) => {
      const headings = bodyContent.querySelectorAll(
        `${SELECTORS.H2}:not(${excludedSelectors})`
      );

      headings.forEach((heading) => {
        const h2Text = heading.textContent;
        const idText = h2Text.replace(/[^a-zA-Z0-9\-_]/g, '').toLowerCase();

        const headingCopy = heading.cloneNode(true);
        headingCopy.id = idText;

        heading.replaceWith(headingCopy);

        const anchorTag = `<a data-id="${idText}" tabIndex="0">${h2Text}</a>`;
        const listItem = `<li class="${CLASS_NAMES.INTRODUCTION_LIST_ITEM}">${anchorTag}</li>`;
        this.navigationList.insertAdjacentHTML(`${MISC.BEFOREEND}`, listItem);

        const option = new Option(h2Text, idText);
        this.dropdown.add(option);
      });
    });

    return this;
  }

  /**
   * Handle click events on navigation links
   * @param {Event} event - the event object
   */
  handleLinkClick(event) {
    if (event.target.tagName === 'A') {
      event.preventDefault();
      const id = event.target.getAttribute('data-id');
      this.scrollToHeading(id);
    }
  }

  /**
   * Handle change events on the dropdown
   * @param {Event} event - the event object
   */
  handleDropdownChange(event) {
    const selectedH2Id = event.target.value;
    this.scrollToHeading(selectedH2Id);
  }

  /**
   * Scroll to the specified heading
   * @param {string} id - the id of the heading to scroll to
   */
  scrollToHeading(id) {
    let heading = null;

    this.bodyContents.forEach((bodyContent) => {
      if (!heading) {
        heading = bodyContent.querySelector(`h2[id="${id}"]`);
      }
    });

    if (heading) {
      const top = heading.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: top - 120,
        behavior: 'smooth'
      });
    }
  }
}
